import { format } from "date-fns";

// Invoice generation function
export const generateInvoice = (order) => {
    // Create a new window for the invoice
    const invoiceWindow = window.open('', '_blank');
    
    // Check if window was successfully created
    if (!invoiceWindow) {
      alert('Please allow pop-ups to print invoices');
      return;
    }
    
    // Format date for invoice
    const formatDate = (dateString) => {
      try {
        return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
      } catch (error) {
        return dateString;
      }
    };
    
    // Calculate subtotal, tax, shipping, and total
    const calculateTotals = (items) => {
      const subtotal = items.reduce((sum, item) => sum + (item.unitPrice * item.count), 0);
      const tax = subtotal * 0; // 15% tax rate
      const shipping = 0; // Fixed shipping cost
      const total = subtotal + tax + shipping;
      
      return { subtotal, tax, shipping, total };
    };
    
    // Get payment method text
    const getPaymentMethodText = (method) => {
      switch (method) {
        case '1':
          return 'ONLINE';
        case '2':
          return 'COD (Cash On Delivery)';
        default:
          return `Payment Method ${method}`;
      }
    };
    
    // Get payment status class
    const getPaymentStatusClass = (status) => {
      return status === 'paid' ? 'status-paid' : 'status-pending';
    };
    
    // Get order status class and text
    const getOrderStatusInfo = (status) => {
      switch (status) {
        case 'completed':
          return { class: 'status-completed', text: 'Completed' };
        case 'processing':
          return { class: 'status-processing', text: 'Processing' };
        case 'cancelled':
          return { class: 'status-cancelled', text: 'Cancelled' };
        default:
          return { class: 'status-processing', text: 'Processing' };
      }
    };
    
    // Calculate totals
    const { subtotal, tax, shipping, total } = calculateTotals(order.orderItems);
    
    // Format currency
    const formatCurrency = (amount) => {
      return parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    };

    // Get order status info
    const orderStatusInfo = getOrderStatusInfo(order.orderStatus || 'processing');
    
    // Generate invoice HTML
    const invoiceHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Invoice #${order.orderId} - Medtools</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          :root {
            --primary-color: #2563eb;
            --primary-light: #dbeafe;
            --secondary-color: #475569;
            --border-color: #e2e8f0;
            --success-color: #10b981;
            --success-bg: #d1fae5;
            --warning-color: #f59e0b;
            --warning-bg: #fef3c7;
            --danger-color: #ef4444;
            --danger-bg: #fee2e2;
            --info-color: #3b82f6;
            --info-bg: #dbeafe;
            --processing-color: #6b7280;
            --processing-bg: #f3f4f6;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #334155;
            background-color: #f8fafc;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .invoice-container {
            max-width: 850px;
            margin: 30px auto;
            padding: 40px;
            background-color: #fff;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.03);
          }
          
          .invoice-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 40px;
            padding-bottom: 25px;
            border-bottom: 1px solid var(--border-color);
          }
          
          .invoice-branding {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }
          
          .logo img {
            max-height: 50px;
            width: auto;
          }
          
          .invoice-title-container {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
          }
          
          .invoice-title {
            font-size: 28px;
            font-weight: 700;
            color: var(--primary-color);
            letter-spacing: -0.5px;
            margin-bottom: 8px;
          }
          
          .invoice-id {
            font-size: 16px;
            color: var(--secondary-color);
            font-weight: 500;
          }
          
          .company-details {
            margin-top: 15px;
            text-align: right;
            font-size: 14px;
            color: var(--secondary-color);
          }
          
          .company-name {
            font-weight: 600;
            font-size: 16px;
            margin-bottom: 5px;
            color: #333;
          }
          
          .invoice-details {
            display: flex;
            justify-content: space-between;
            margin-bottom: 40px;
          }
          
          .invoice-details-col {
            flex: 1;
          }
          
          .customer-details {
            margin-right: 60px;
          }
          
          .invoice-info {
            text-align: right;
          }
          
          .details-title {
            font-weight: 600;
            margin-bottom: 12px;
            color: var(--secondary-color);
            text-transform: uppercase;
            font-size: 13px;
            letter-spacing: 0.5px;
          }
          
          .details-item {
            margin-bottom: 10px;
            font-size: 14px;
            line-height: 1.6;
          }
          
          .details-item strong {
            font-weight: 600;
            color: #1e293b;
          }
          
          .status-label {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .status-paid {
            background-color: var(--success-bg);
            color: var(--success-color);
          }
          
          .status-pending {
            background-color: var(--warning-bg);
            color: var(--warning-color);
          }
          
          .status-completed {
            background-color: var(--info-bg);
            color: var(--info-color);
          }
          
          .status-processing {
            background-color: var(--processing-bg);
            color: var(--processing-color);
          }
          
          .status-cancelled {
            background-color: var(--danger-bg);
            color: var(--danger-color);
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 35px;
            border-radius: 6px;
            overflow: hidden;
          }
          
          thead {
            background-color: #f8fafc;
          }
          
          th {
            background-color: #f1f5f9;
            padding: 12px 15px;
            text-align: left;
            font-size: 13px;
            font-weight: 600;
            text-transform: uppercase;
            color: var(--secondary-color);
            letter-spacing: 0.5px;
            border-bottom: 1px solid var(--border-color);
          }
          
          td {
            padding: 15px;
            border-bottom: 1px solid var(--border-color);
            font-size: 14px;
            vertical-align: top;
          }
          
          tr:last-child td {
            border-bottom: none;
          }
          
          .text-right {
            text-align: right;
          }
          
          .totals-container {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 40px;
          }
          
          .totals {
            width: 45%;
            border-top: 1px solid var(--border-color);
            padding-top: 20px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 14px;
          }
          
          .total-row.grand-total {
            font-weight: 700;
            font-size: 16px;
            color: var(--primary-color);
            border-top: 2px solid var(--border-color);
            padding-top: 12px;
            margin-top: 10px;
          }
          
          .notes {
            margin-top: 40px;
            font-size: 14px;
            color: var(--secondary-color);
            background-color: #f8fafc;
            padding: 20px;
            border-radius: 6px;
            border-left: 4px solid var(--primary-color);
          }
          
          .notes strong {
            font-weight: 600;
            color: #334155;
            display: block;
            margin-bottom: 8px;
          }
          
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 13px;
            color: var(--secondary-color);
            padding-top: 20px;
            border-top: 1px solid var(--border-color);
          }
          
          .print-buttons {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 30px;
          }
          
          .btn {
            padding: 10px 20px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 500;
            font-size: 14px;
            transition: background-color 0.2s, transform 0.1s;
          }
          
          .btn:hover {
            opacity: 0.9;
            transform: translateY(-1px);
          }
          
          .btn:active {
            transform: translateY(0);
          }
          
          .btn-primary {
            background-color: var(--primary-color);
            color: white;
          }
          
          .btn-secondary {
            background-color: #64748b;
            color: white;
          }
          
          @media print {
            body {
              background-color: #fff;
              margin: 0;
              padding: 0;
            }
            
            .invoice-container {
              margin: 0;
              padding: 20px;
              max-width: 100%;
              box-shadow: none;
              border: none;
            }
            
            .no-print {
              display: none !important;
            }
            
            /* Ensure page breaks don't occur in the middle of content */
            table, tr, td, th, tbody, thead, tfoot {
              page-break-inside: avoid;
            }
            
            .notes, .totals-container {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="invoice-header">
            <div class="invoice-branding">
              <div class="logo">
                <img src="https://res.cloudinary.com/dt5upxmm8/image/upload/v1743933804/logo_bkymya.png" alt="Medtools Logo">
              </div>
              <div class="company-details">
                <div class="company-name">medtools.lk</div>
                <div>123 Main Street, Colombo</div>
                <div>medtools.lk@gmail.com</div>
                <div>076 837 2991</div>
              </div>
            </div>
            
            <div class="invoice-title-container">
              <div class="invoice-title">INVOICE</div>
              <div class="invoice-id">#INV-${order.orderId}</div>
            </div>
          </div>
          
          <div class="invoice-details">
            <div class="invoice-details-col customer-details">
              <div class="details-title">Bill To</div>
              <div class="details-item"><strong>${order.firstName} ${order.lastName}</strong></div>
              <div class="details-item">${order.shippingAddress}</div>
              <div class="details-item">${order.city}</div>
              <div class="details-item">Phone: ${order.phone}</div>
            </div>
            
            <div class="invoice-details-col invoice-info">
              <div class="details-title">Invoice Details</div>
              <div class="details-item"><strong>Date Issued:</strong> ${formatDate(order.createdAt)}</div>
              <div class="details-item"><strong>Payment Method:</strong> ${getPaymentMethodText(order.paymentMethod)}</div>
              <div class="details-item">
                <strong>Payment Status:</strong> 
                <span class="status-label ${getPaymentStatusClass(order.paymentStatus)}">
                  ${order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>
              <div class="details-item">
                <strong>Order Status:</strong> 
                <span class="status-label ${orderStatusInfo.class}">
                  ${orderStatusInfo.text}
                </span>
              </div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.orderItems.map(item => `
                <tr>
                  <td>${item.product}</td>
                  <td>${item.title}</td>
                  <td>${item.count}</td>
                  <td>LKR ${formatCurrency(item.unitPrice)}</td>
                  <td class="text-right">LKR ${formatCurrency(item.unitPrice * item.count)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="totals-container">
            <div class="totals">
              <div class="total-row">
                <div>Subtotal</div>
                <div>LKR ${formatCurrency(subtotal)}</div>
              </div>
              <div class="total-row">
                <div>Tax</div>
                <div>LKR ${formatCurrency(tax)}</div>
              </div>
              <div class="total-row">
                <div>Shipping</div>
                <div>LKR ${formatCurrency(shipping)}</div>
              </div>
              <div class="total-row grand-total">
                <div>Grand Total</div>
                <div>LKR ${formatCurrency(total)}</div>
              </div>
            </div>
          </div>
          
          <div class="notes">
            <strong>Thank you for your purchase!</strong>
            <p>If you have any questions about this invoice, please contact our customer support team at medtools.lk@gmail.com or call us at 076 837 2991.</p>
          </div>
          
          <div class="footer">
            <p>This invoice was generated on ${formatDate(new Date())}.</p>
            <p>© ${new Date().getFullYear()} medtools.lk. All rights reserved.</p>
          </div>
          
          <div class="no-print print-buttons">
            <button onclick="window.print()" class="btn btn-primary">
              Print Invoice
            </button>
            <button onclick="window.close()" class="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Write the HTML to the new window
    invoiceWindow.document.open();
    invoiceWindow.document.write(invoiceHTML);
    invoiceWindow.document.close();
};